import { datadogRum } from "@datadog/browser-rum";
import * as FullStory from "@fullstory/browser";

/**
 * Convert a Title Case event name to snake_case for Datadog RUM action names.
 * Examples:
 *   "Application Resubmitted" -> "application_resubmitted"
 *   "Application Withdrawn for Edit" -> "application_withdrawn_for_edit"
 */
const toDatadogActionName = (eventName: string): string =>
  eventName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");

/**
 * FullStory v1 requires type suffixes on property names so they're indexed
 * and searchable in the dashboard. This helper infers the correct suffix
 * from the JS runtime type and renames the key. Existing keys that already
 * end in a FullStory suffix are passed through unchanged.
 */
const FS_SUFFIX_REGEX =
  /_(str|int|real|bool|date|obj|strs|ints|reals|bools|dates|objs)$/;

const getFsSuffix = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return "_strs";
    const first = value[0];
    if (typeof first === "string") return "_strs";
    if (typeof first === "number")
      return Number.isInteger(first) ? "_ints" : "_reals";
    if (typeof first === "boolean") return "_bools";
    if (first instanceof Date) return "_dates";
    return "_objs";
  }
  if (value instanceof Date) return "_date";
  if (typeof value === "string") return "_str";
  if (typeof value === "boolean") return "_bool";
  if (typeof value === "number")
    return Number.isInteger(value) ? "_int" : "_real";
  if (typeof value === "object") return "_obj";
  return null;
};

const toFullStoryProperties = (
  properties: Record<string, unknown>
): Record<string, unknown> => {
  if (!properties || typeof properties !== "object") return properties;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) continue;
    if (FS_SUFFIX_REGEX.test(key)) {
      result[key] = value;
      continue;
    }
    const suffix = getFsSuffix(value);
    if (!suffix) {
      result[key] = value;
      continue;
    }
    result[`${key}${suffix}`] = value;
  }
  return result;
};

/**
 * Track a custom event in both Datadog RUM and FullStory.
 *
 * Properties are passed to Datadog RUM as-is (RUM has no type suffix
 * requirement). For FullStory, property keys are automatically suffixed
 * with the correct type marker (_str, _int, _real, _bool, _date, etc.)
 * so they're indexed and searchable in the FullStory dashboard.
 *
 * @param eventName - Title Case event name (e.g., "Application Withdrawn for Edit")
 * @param properties - Event properties (plain keys, no suffixes needed)
 *
 * @example
 *   trackEvent('Application Withdrawn for Edit', { applicationId: 123 })
 *   // -> RUM: application_withdrawn_for_edit { applicationId: 123 }
 *   // -> FS:  Application Withdrawn for Edit { applicationId_int: 123 }
 */
export const trackEvent = (
  eventName: string,
  properties: Record<string, unknown> = {}
): void => {
  if (!eventName) return;

  try {
    datadogRum.addAction(toDatadogActionName(eventName), properties);
  } catch (e) {
    // Silently swallow — analytics should never break the app
  }

  try {
    FullStory?.event?.(eventName, toFullStoryProperties(properties));
  } catch (e) {
    // Silently swallow
  }
};
